import {
  bloodSugarMedicineTypes,
  diabetesTypes,
  DanishMunicipality,
  Sex,
  PatientHistory,
  activeTaskStatus,
  IConsultationStatusDetails,
  PatientHistoryElement,
  patientConsentToSmsType,
  PRO_TASK_CONSULTATION_TYPE_EXT_URL,
  proTaskConsultationType,
  evalConsentType
} from "./types";
import { CPR } from "./cpr";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { TranslateService } from "@ngx-translate/core";
import { calculateWho5 } from "../utils/calculate-who5";
import { Injectable } from "@angular/core";
import { DashboardService } from "../services/dashboard.service";
import * as moment from "moment";
import { extractResponseValidationExt } from "../utils/extract-validation-ext";
import { extractConsultationTypeExt } from "../utils/extract-consultation-type-ext";

export interface IProTask {
  task: fhir.Task;
  procedureRequest: fhir.ProcedureRequest;
  response: fhir.QuestionnaireResponse;
}

export interface IDiaprofilPatient {
  fhirId: string;
  userName: string;
  fhirResource: fhir.Patient;
  socialSecurityNumber: string;
  fullName: string;
  homePath: string;
  language: string;
  mobileNumber: string;
  organizationUid: string;
  bloodSugarMedicineTypes: bloodSugarMedicineTypes[];
  consentToSms: patientConsentToSmsType;
  diagnosis: diabetesTypes;
  diagnosisYear: string;
  evalConsent: evalConsentType;
  evalConsentResponseUuid: string;
  municipality: DanishMunicipality;
  proTasks: IProTask[];
  consultations: IConsultationStatusDetails[];
  questionnaireResponses?: fhir.QuestionnaireResponse[];
  activeTask?: fhir.Task;
  isDeactivated: boolean;
  deactivationReason: string;
  deactivationTimestamp: string;
  evalStudyParticipantId: string;
}

export function diaprofilPatientFactory(
  config: IDiaprofilPatient,
  translate: TranslateService,
  questionnaireResponseService: QuestionnaireResponseService,
  dashboard: DashboardService
) {
  return new DiaprofilPatient(
    config,
    translate,
    questionnaireResponseService,
    dashboard
  );
}

@Injectable()
export class DiaprofilPatient implements IDiaprofilPatient {
  fhirId: string;
  userName: string;
  fhirResource: fhir.Patient;
  socialSecurityNumber: string;
  fullName: string;
  homePath: string;
  language: string;
  mobileNumber: string;
  organizationUid: string;
  bloodSugarMedicineTypes: bloodSugarMedicineTypes[];
  consentToSms: patientConsentToSmsType;
  diagnosis: diabetesTypes;
  diagnosisYear: string;
  evalConsent: evalConsentType;
  evalConsentResponseUuid: string;
  municipality: DanishMunicipality;
  questionnaireResponses: fhir.QuestionnaireResponse[];
  proTasks: IProTask[];
  consultations: IConsultationStatusDetails[];
  activeTask: fhir.Task;
  cpr: CPR;
  address: fhir.Address = null;
  isDeactivated: boolean;
  deactivationReason: string;
  deactivationTimestamp: string;
  evalStudyParticipantId: string;
  constructor(
    public config: IDiaprofilPatient,
    public translate: TranslateService,
    private questionnaireResponseService: QuestionnaireResponseService,
    private dashboard: DashboardService
  ) {
    this.fhirId = config.fhirId;
    this.userName = config.userName;
    this.fhirResource = config.fhirResource;
    this.socialSecurityNumber = config.socialSecurityNumber;
    this.cpr = new CPR(this.socialSecurityNumber);
    this.fullName = config.fullName;
    this.homePath = config.homePath;
    this.language = config.language;
    this.mobileNumber = config.mobileNumber;
    this.organizationUid = config.organizationUid;
    this.bloodSugarMedicineTypes = config.bloodSugarMedicineTypes;
    this.consentToSms = config.consentToSms;
    this.diagnosis = config.diagnosis;
    this.diagnosisYear = config.diagnosisYear;
    this.evalConsent = config.evalConsent || "NOT_ANSWERED";
    this.evalConsentResponseUuid = config.evalConsentResponseUuid;
    this.municipality = config.municipality;
    this.isDeactivated = config.isDeactivated;
    this.deactivationReason = config.deactivationReason;
    this.deactivationTimestamp = config.deactivationTimestamp;
    this.evalStudyParticipantId = config.evalStudyParticipantId;
    this.proTasks = config.proTasks || [];
    if (config.hasOwnProperty("questionnaireResponses")) {
      this.questionnaireResponses = config.questionnaireResponses;
      if (Array.isArray(this.questionnaireResponses)) {
        this.questionnaireResponses = this.questionnaireResponses.sort((a, b) =>
          a.authored < b.authored ? -1 : 1
        );
      }
    } else {
      this.questionnaireResponses = this.proTasks
        .map(proTask => proTask.response || null)
        .filter(response => response !== null)
        .sort((a, b) => (a.authored < b.authored ? -1 : 1));
    }
    this.consultations = config.consultations || [];
    if (config.hasOwnProperty("activeTask")) {
      this.activeTask = config.activeTask;
    } else {
      const activeTask = this.proTasks
        .map(proTask => proTask.task || null)
        .find(task => task !== null && task.status === "ready");
      this.activeTask = activeTask !== undefined ? activeTask : null;
    }
    if (
      this.config.fhirResource !== null &&
      this.config.fhirResource !== undefined
    ) {
      this.address =
        config.fhirResource.address !== null &&
        config.fhirResource.address !== undefined &&
        config.fhirResource.address.length > 0
          ? config.fhirResource.address[0]
          : null;
    }
  }

  get birthDate(): Date {
    return this.cpr.birthdate;
  }

  get age(): number {
    return this.cpr.age;
  }

  get sex(): Sex {
    return this.cpr.sex;
  }

  get firstName(): string {
    return this.fullName.split(" ")[0];
  }

  get lastName(): string {
    return this.fullName
      .split(" ")
      .slice(-1)
      .join(" ");
  }

  get isPumpUser(): boolean {
    if (!Array.isArray(this.bloodSugarMedicineTypes)) {
      return null;
    }
    return this.bloodSugarMedicineTypes.includes("INSULIN_PUMP");
  }

  get numberOfResponses(): number {
    return this.questionnaireResponses.length;
  }

  get latestProTask(): IProTask {
    const length = this.proTasks.length;
    if (length === 0) {
      return null;
    }
    const latestProTask = this.proTasks[length - 1];
    return latestProTask;
  }

  get latestCompletedProTask(): IProTask {
    const completedProTask = this.proTasks.find(
      /*
       * We look at response status instead of proTask status to ensure
       * that we catch answers where eval tasks have not been completed
       * leaving parent task still "ready"
       */
      proTask =>
        proTask.response !== null && proTask.response.status === "completed"
    );
    return completedProTask !== undefined ? completedProTask : null;
  }

  get latestResponse(): fhir.QuestionnaireResponse {
    return this.latestCompletedProTask !== null
      ? this.latestCompletedProTask.response
      : null;
  }

  get latestProConsultationDate(): string {
    const proTask = this.latestCompletedProTask;
    if (proTask === null) {
      return null;
    }
    return proTask.task.restriction.period.end;
  }

  get latestProConsultationParentTaskStatus(): string {
    const proTask = this.latestCompletedProTask;
    return proTask.task.status;
  }

  get latestProConsultationType() {
    const proTask = this.latestCompletedProTask;
    if (proTask === null) {
      return null;
    }
    return extractConsultationTypeExt(proTask.task);
  }

  get latestProConsultationResponseDate(): string {
    const proTask = this.latestCompletedProTask;
    if (proTask === null || proTask.response === null) {
      return null;
    }
    try {
      return proTask.response.authored;
    } catch {
      console.error("COULD NOT FIND AUTHORED DATE FOR RESPONSE");
    }
  }

  get dateOfLatestResponse(): string {
    // Function has been deprecated but kept due to naming dependency
    return this.latestProConsultationResponseDate;
  }

  get latestProConsultationStatus(): IConsultationStatusDetails {
    const proTask = this.latestCompletedProTask;
    if (proTask === null || proTask.response === null) {
      return null;
    }
    return extractResponseValidationExt(proTask.response);
  }

  get latestProConsultationHcp(): string {
    const evalStatus = this.latestProConsultationStatus;
    return evalStatus !== null ? evalStatus.hcpInitials : null;
  }

  get latestWho5(): number {
    if (this.latestResponse === null) {
      return null;
    }
    const answers = this.questionnaireResponseService.extractAnswersFromQuestionnaireResponse(
      this.latestResponse
    );
    const score = calculateWho5(answers).score;
    return score;
  }

  get dateOfActiveTask(): string {
    return this.activeTask !== null
      ? this.activeTask.restriction.period.end
      : null;
  }

  get typeOfActiveTask(): proTaskConsultationType {
    if (this.activeTask === null) {
      return null;
    }
    if (
      this.activeTask.hasOwnProperty("extension") &&
      this.activeTask.extension.length > 0
    ) {
      const consultationTypeExt = this.activeTask.extension.find(
        ext => ext.url === PRO_TASK_CONSULTATION_TYPE_EXT_URL
      );
      if (consultationTypeExt !== undefined) {
        return consultationTypeExt.valueCode as proTaskConsultationType;
      }
    }
    return null;
  }

  get daysUntilNextConsultation(): number {
    const consultationDate =
      this.activeTask !== null ? this.activeTask.restriction.period.end : null;
    if (consultationDate === null) {
      return null;
    }
    return moment(consultationDate).diff(moment(), "days");
  }

  get statusOfActiveTask(): activeTaskStatus {
    const daysUntilConsultation = this.daysUntilNextConsultation;
    if (daysUntilConsultation === null) {
      return null;
    }
    if (daysUntilConsultation > 1) {
      return "NORMAL";
    } else if (daysUntilConsultation <= 1 && daysUntilConsultation >= 0) {
      return "CLOSE_TO_EXPIRATION";
    } else {
      return "EXPIRED";
    }
  }

  get patientHistory(): PatientHistory {
    return this.questionnaireResponses.map(response => {
      const historyElem: PatientHistoryElement = {
        responseResource: response,
        items: this.dashboard.getWidgetsWithAnswers(response)
      };
      return historyElem;
    });
  }

  getPrecedingResponse(questionnaireResponse: fhir.QuestionnaireResponse) {
    if (this.questionnaireResponses.length === 1) {
      return null;
    } else {
      const curIndex = this.questionnaireResponses.findIndex(
        response => response.id === questionnaireResponse.id
      );
      return curIndex !== 0 ? this.questionnaireResponses[curIndex - 1] : null;
    }
  }

  getNextResponse(questionnaireResponse: fhir.QuestionnaireResponse) {
    const length = this.questionnaireResponses.length;
    if (length === 1) {
      return null;
    } else {
      const curIndex = this.questionnaireResponses.findIndex(
        response => response.id === questionnaireResponse.id
      );
      if (length < curIndex + 2) {
        return null;
      }
      return this.questionnaireResponses[curIndex + 1];
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/models/diaprofil-patient.ts